// Diagnóstico CIE-10 con marca (Presuntivo/Definitivo, o el catálogo que
// traiga cada formulario) — la Serie 028 (028A/028B/028C, Fase 2) es el
// primer consumidor que necesita esa marca al lado del selector CIE-10 de
// SelectorDiagnosticoCie10.tsx (016/113/114/115/116-cert/038 solo guardan el
// diagnóstico, sin marca). Las opciones de marca varían por formulario según
// el instructivo oficial (028A/028C: PRE/DEF; 028B trae una 3ra, "GE") — se
// reciben por prop en vez de hardcodear un único catálogo.
import type { CodeableConcept } from '@medplum/fhirtypes';
import type { JSX } from 'react';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { conCodigoCie } from './camposSimplesDatos';
import { codeDeDiagnostico, SelectorDiagnosticoCie10, type DiagnosticoCie10Valor } from './SelectorDiagnosticoCie10';

export interface DiagnosticoConMarca {
  diagnostico: DiagnosticoCie10Valor;
  marca: string;
}

export function diagnosticoConMarcaVacio(): DiagnosticoConMarca {
  return { diagnostico: { texto: '', cie10: null }, marca: '' };
}

/** Texto para el PDF/historia: "<diagnóstico> (<CIE>) (<texto de la marca>)"; sin código o sin marca, se omite esa parte. */
export function textoDiagnosticoConMarca(valor: DiagnosticoConMarca, marcas: readonly OpcionMarca[]): string {
  const texto = conCodigoCie(valor.diagnostico);
  if (!texto) return '';
  const marcaTexto = marcas.find((m) => m.valor === valor.marca)?.texto;
  return marcaTexto ? `${texto} (${marcaTexto})` : texto;
}

/**
 * Marca PRE/DEF del papel -> `Condition.verificationStatus` FHIR (PRE = presuntivo = `provisional`, DEF = definitivo =
 * `confirmed`). Sin marca no se inventa un estado: devuelve `undefined`.
 */
export function verificationStatusDeMarca(marca: string): CodeableConcept | undefined {
  const codigo = marca === 'pre' ? 'provisional' : marca === 'def' ? 'confirmed' : undefined;
  if (!codigo) return undefined;
  return { coding: [{ system: 'http://terminology.hl7.org/CodeSystem/condition-ver-status', code: codigo, display: codigo === 'provisional' ? 'Provisional' : 'Confirmed' }] };
}

export { codeDeDiagnostico };

export interface OpcionMarca {
  readonly valor: string;
  readonly texto: string;
}

export interface SelectorDiagnosticoConMarcaProps {
  readonly label: string;
  readonly marcaLabel: string;
  readonly marcas: readonly OpcionMarca[];
  readonly value: DiagnosticoConMarca;
  readonly onChange: (valor: DiagnosticoConMarca) => void;
}

export function SelectorDiagnosticoConMarca({ label, marcaLabel, marcas, value, onChange }: SelectorDiagnosticoConMarcaProps): JSX.Element {
  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_auto] sm:items-end">
      <SelectorDiagnosticoCie10 label={label} value={value.diagnostico} onChange={(diagnostico) => onChange({ ...value, diagnostico })} />
      <div className="flex flex-col gap-1">
        <Label>{marcaLabel}</Label>
        <Select value={value.marca || undefined} onValueChange={(marca) => onChange({ ...value, marca })}>
          <SelectTrigger className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {marcas.map((m) => (
              <SelectItem key={m.valor} value={m.valor}>
                {m.texto}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
